import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../i18n';
import { api } from '../lib/api';
import { useAgentStatus, useCustomer, useCustomerActivity, useDocuments } from '../lib/queries';
import { fmtDate, fmtDateTime, fmtMoney, fmtRelative } from '../lib/format';
import type { Address, Customer, CustomerBuying } from '../lib/types';
import { Button, Callout, Checkbox, CustomerStatusBadge, Dialog, Empty, ErrorState, Field, Input, Ltr, QuoteBadge, Tabs, useConfirm, useToast } from '../components/ui';
import { DocTable } from '../components/DocTable';
import { UploadButton } from '../components/UploadDialog';
import { AddressFields, addressToPayload, emptyAddress, formatAddress, type AddressDraft } from '../components/AddressFields';
import { IconMail, IconPlus, IconPrinter, IconStar, IconTrash, IconUpload, IconX } from '../components/icons';
import { QuoteImportButton } from '../components/QuoteImport';
import { useCan } from '../lib/auth';
import { ProductChip } from './FollowUps';
import { useOpenDocument } from '../components/Layout';
import { PriceList } from '../components/PriceList';
import { CustomerProfit } from '../components/CustomerProfit';
import { CustomerArchiveInvoices } from './Archive';
import { CustomerOrders, CustomerQuotes, NewOrderButton } from '../components/Orders';
import { ListReaderDialog } from '../components/ListReader';

type Tab = 'overview' | 'details' | 'quotes' | 'invoices' | 'delivery' | 'prices' | 'profit' | 'orders' | 'activity';

export function CustomerProfilePage() {
  const id = Number(useParams().id);
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'overview';
  const { data: c, isLoading, error, refetch } = useCustomer(id);
  // A parent customer's file shows its branches' documents too (no effect when there are none).
  const [branches, setBranches] = useState(true);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [readerOpen, setReaderOpen] = useState(false);
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const qc = useQueryClient();
  const can = useCan();

  const setTab = (k: Tab) => {
    const next = new URLSearchParams(params);
    if (k === 'overview') next.delete('tab');
    else next.set('tab', k);
    setParams(next, { replace: true });
  };

  if (error) return <div className="panel"><ErrorState error={error} onRetry={() => refetch()} /></div>;
  if (isLoading || !c) {
    return (
      <div className="stack">
        <span className="skeleton" style={{ width: 280, height: 22 }} />
        <div className="kpis" style={{ height: 88 }} />
      </div>
    );
  }

  const remove = async () => {
    if (!(await confirm({ title: t.profile.deleteConfirm, body: 'ניתן למחוק רק לקוח ללא מסמכים. ללקוח עם היסטוריה — סמנו "לא פעיל".', danger: true, confirmLabel: t.common.delete }))) return;
    try {
      await api(`/api/customers/${id}`, { method: 'DELETE' });
      for (const k of ['customers', 'customer-options', 'dashboard']) qc.invalidateQueries({ queryKey: [k] });
      toast.success('הלקוח נמחק');
      navigate('/customers');
    } catch (e) {
      toast.error(e);
    }
  };

  const hasBranches = c.children.length > 0;
  const s = c.stats;

  return (
    <>
      <div className="page-head">
        <div style={{ minWidth: 0 }}>
          <div className="crumbs">
            <Link to="/customers">{t.customers.title}</Link>
            {c.parent ? <> / <Link to={`/customers/${c.parent.id}`}>{c.parent.businessName}</Link></> : null}
          </div>
          <div className="row wrap" style={{ gap: 10 }}>
            <h1>{c.businessName}</h1>
            <CustomerStatusBadge status={c.status} />
          </div>
          <div className="sub row wrap" style={{ gap: 14 }}>
            {c.companyNumber ? <span>ח.פ <Ltr className="tabular">{c.companyNumber}</Ltr></span> : <span>ללא ח.פ</span>}
            <span>{c.paymentTermsLabel}</span>
            {c.phone ? <Ltr>{c.phone}</Ltr> : null}
            {c.email ? <a href={`mailto:${c.email}`}><bdi>{c.email}</bdi></a> : null}
          </div>
        </div>
        <div className="row wrap">
          {can('admin') ? (
            <Button onClick={() => setBackfillOpen(true)}>
              <IconMail /> {t.profile.importHistory}
            </Button>
          ) : null}
          {can('documents.edit') ? (
            <UploadButton customerId={c.id}>
              <IconUpload /> {t.profile.upload}
            </UploadButton>
          ) : null}
          {can('finance.view') ? (
            <Link to={`/customers/${c.id}/statement`} className="btn">
              <IconPrinter /> כרטסת
            </Link>
          ) : null}
          {can('customers.edit') ? <Link to={`/customers/${c.id}/edit`} className="btn">{t.common.edit}</Link> : null}
          {can('orders.edit') ? <Button onClick={() => setReaderOpen(true)} title="מדביקים תמונה / PDF / טקסט של רשימת מוצרים — הסוכן מתאים לקטלוג ופותח הצעת מחיר">הצעת מחיר מרשימה</Button> : null}
          {can('orders.edit') ? <NewOrderButton customerId={c.id} variant="primary"><IconPlus /> הזמנה חדשה</NewOrderButton> : null}
        </div>
      </div>

      <Tabs<Tab>
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'overview', label: t.profile.tabs.overview },
          { key: 'details', label: t.profile.tabs.details },
          { key: 'quotes', label: t.profile.tabs.quotes, count: s.quoteCount },
          { key: 'invoices', label: t.profile.tabs.invoices, count: s.invoiceCount + s.receiptCount + s.creditNoteCount },
          { key: 'delivery', label: t.profile.tabs.delivery, count: s.deliveryNoteCount },
          ...(can('catalog.edit') ? [{ key: 'prices' as const, label: 'מחירון' }] : []),
          ...(can('profit.view') ? [{ key: 'profit' as const, label: 'רווחיות' }] : []),
          ...(can('orders.edit') ? [{ key: 'orders' as const, label: 'הזמנות' }] : []),
          { key: 'activity', label: t.profile.tabs.activity },
        ]}
      />

      {hasBranches && (tab === 'quotes' || tab === 'invoices' || tab === 'delivery') ? (
        <div style={{ marginBottom: 12 }}>
          <Checkbox checked={branches} onChange={setBranches}>{t.profile.includeBranches} ({c.children.length})</Checkbox>
        </div>
      ) : null}

      {tab === 'overview' ? <Overview c={c} goTab={setTab} /> : null}
      {tab === 'details' ? <Details c={c} onDelete={remove} /> : null}
      {tab === 'quotes' ? <QuotesTab c={c} branches={branches} /> : null}
      {tab === 'invoices' ? <InvoicesTab c={c} branches={branches} /> : null}
      {tab === 'delivery' ? <DeliveryTab c={c} branches={branches} /> : null}
      {tab === 'prices' && can('catalog.edit') ? <PriceList customerId={c.id} parentName={c.parent?.businessName} /> : null}
      {tab === 'profit' && can('profit.view') ? <CustomerProfit customerId={c.id} /> : null}
      {tab === 'orders' && can('orders.edit') ? (
        <div className="stack">
          <CustomerQuotes customerId={c.id} />
          <CustomerOrders customerId={c.id} />
        </div>
      ) : null}
      {tab === 'activity' ? <ActivityTab id={c.id} /> : null}

      {backfillOpen ? <BackfillDialog c={c} onClose={() => setBackfillOpen(false)} /> : null}
      {readerOpen ? <ListReaderDialog customerId={c.id} onClose={() => setReaderOpen(false)} /> : null}
    </>
  );
}

// ------------------------------------------------------------------ overview

function Overview({ c, goTab }: { c: Customer; goTab: (k: Tab) => void }) {
  const s = c.stats;
  // Every document of the customer (and its branches), newest first — nothing is hidden behind another tab.
  const hasBranches = c.children.length > 0;
  const { data: recent, isLoading } = useDocuments({ customerId: c.id, includeBranches: hasBranches, sort: 'issueDate', dir: 'desc', pageSize: 200 });
  const open = useOpenDocument();
  const primary = c.addresses.find((a) => a.isPrimary);
  return (
    <div className="stack">
      <section className="kpis">
        <div className="kpi">
          <div className="kpi-label">{t.profile.totalInvoiced}</div>
          <div className="kpi-value">{fmtMoney(s.totalInvoiced)}</div>
          <div className="kpi-meta">{s.invoiceCount} {t.profile.invoices}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">{t.profile.outstanding}</div>
          <div className="kpi-value" style={(s.overdue ?? 0) > 0 ? { color: 'var(--bad)' } : undefined}>{fmtMoney(s.outstanding)}</div>
          <div className="kpi-meta">{(s.overdue ?? 0) > 0 ? `${t.profile.overdue}: ${fmtMoney(s.overdue)}` : 'אין חוב באיחור'}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">{t.profile.quotes}</div>
          <div className="kpi-value">{s.quoteCount}</div>
          <div className="kpi-meta">{s.deliveryNoteCount} תעודות משלוח</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">חשבונית אחרונה</div>
          <div className="kpi-value" style={{ fontSize: 17 }}>{fmtDate(s.lastInvoiceDate)}</div>
          <div className="kpi-meta">{c.paymentTermsLabel}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">{t.profile.lastActivity}</div>
          <div className="kpi-value" style={{ fontSize: 15, lineHeight: '22px', marginTop: 6 }}>{s.lastActivity ? fmtRelative(s.lastActivity.createdAt) : '—'}</div>
          <div className="kpi-meta ellipsis">{s.lastActivity?.description}</div>
        </div>
      </section>

      {s.unbilledDeliveryNotes > 0 ? (
        <Callout tone="info" title={`${s.unbilledDeliveryNotes} ${t.profile.unbilled}`} action={<Button size="sm" onClick={() => goTab('delivery')}>הצגה</Button>}>
          תעודות משלוח שעדיין לא נכללו בחשבונית מרכזת.
        </Callout>
      ) : null}
      {(s.unallocatedCredit ?? 0) > 0.009 ? (
        <Callout tone="warn" title={`${t.profile.unallocated}: ${fmtMoney(s.unallocatedCredit)}`} action={<Button size="sm" onClick={() => goTab('invoices')}>בדיקה</Button>}>
          קבלות או זיכויים שהמערכת לא הצליחה לקשר לחשבונית מסוימת. ניתן לשייך אותם ידנית מתוך המסמך.
        </Callout>
      ) : null}

      <div className="grid-main-side">
        <div className="stack">
          <section className="panel">
            <div className="panel-head">
              <h2>{t.profile.primaryQuote}</h2>
              <span className="spacer" />
              <Button size="sm" variant="ghost" onClick={() => goTab('quotes')}>כל ההצעות</Button>
            </div>
            {c.primaryQuote ? (
              <button className="candidate" style={{ border: 0, borderRadius: 0 }} onClick={() => open(c.primaryQuote!.id)}>
                <IconStar filled style={{ color: 'var(--accent)' }} />
                <div style={{ flex: 1 }}>
                  <div className="strong">הצעת מחיר <Ltr>{c.primaryQuote.number}</Ltr></div>
                  <div className="small muted">{fmtDate(c.primaryQuote.issueDate)}{c.primaryQuote.description ? ` · ${c.primaryQuote.description}` : ''}</div>
                </div>
                <QuoteBadge status={c.primaryQuote.status} />
                <span className="tabular strong">{fmtMoney(c.primaryQuote.total)}</span>
              </button>
            ) : (
              <div className="panel-body muted small">{t.profile.noPrimaryQuote}. הצעת המחיר הראשונה שתתקבל תסומן כראשית; ניתן לשנות זאת מתוך כל הצעה.</div>
            )}
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2>כל המסמכים</h2>
              <span className="spacer" />
              {recent ? <span className="small muted tabular">{recent.total} מסמכים{recent.total > recent.items.length ? ` · מוצגים ${recent.items.length} האחרונים` : ''}</span> : null}
            </div>
            <div style={{ maxHeight: 560, overflowY: 'auto' }}>
            <DocTable
              docs={recent?.items}
              loading={isLoading}
              columns={['type', 'number', 'date', ...(hasBranches ? (['customer'] as const) : []), 'total', 'margin', 'payment']}
              empty={<Empty title="עדיין אין מסמכים ללקוח" body="מסמכים שתפיקו ללקוח במורנינג ייכנסו לכאן אוטומטית. ניתן גם לייבא היסטוריה מהמייל או להעלות מסמך ידנית." />}
            />
            </div>
          </section>
        </div>

        <aside className="stack">
          <section className="panel">
            <div className="panel-head"><h2>פרטי קשר</h2></div>
            <div className="panel-body">
              <dl className="kv">
                <dt>{t.customerForm.companyNumber}</dt>
                <dd>{c.companyNumber ? <Ltr className="tabular">{c.companyNumber}</Ltr> : <span className="muted">—</span>}</dd>
                <dt>{t.customerForm.contact}</dt>
                <dd>{c.contactName ?? <span className="muted">—</span>}</dd>
                <dt>{t.customerForm.phone}</dt>
                <dd>{c.phone ? <a href={`tel:${c.phone}`}><Ltr>{c.phone}</Ltr></a> : <span className="muted">—</span>}</dd>
                <dt>{t.customerForm.email}</dt>
                <dd>{c.email ? <a href={`mailto:${c.email}`}><bdi>{c.email}</bdi></a> : <span className="muted">—</span>}</dd>
                <dt>{t.customerForm.terms}</dt>
                <dd>{c.paymentTermsLabel}</dd>
                <dt>{t.address.primary}</dt>
                <dd>{primary ? <>{primary.label}<div className="muted small">{formatAddress(primary)}</div></> : <span className="muted">—</span>}</dd>
              </dl>
            </div>
          </section>
          <BuyingPanel c={c} />
          {c.children.length || c.parent ? <BranchesPanel c={c} /> : null}
          {c.notes ? (
            <section className="panel">
              <div className="panel-head"><h2>{t.common.notes}</h2></div>
              <div className="panel-body" style={{ whiteSpace: 'pre-wrap' }}>{c.notes}</div>
            </section>
          ) : null}
        </aside>
      </div>
    </div>
  );
}

/** Ordering rhythm and product families, with what is worth offering now. */
function BuyingPanel({ c }: { c: Customer }) {
  const { data } = useQuery({ queryKey: ['customer', c.id, 'buying'], queryFn: () => api<CustomerBuying>(`/api/customers/${c.id}/buying`) });
  const qc = useQueryClient();
  const toast = useToast();
  if (!data || data.orders === 0) return null;
  const statusText = {
    due: 'עבר את קצב ההזמנות הרגיל — כדאי לפנות',
    dormant: 'רדום — לא הזמין תקופה ארוכה',
    on_track: 'מזמין בקצב הרגיל',
    new: 'לקוח חדש',
    none: '',
  }[data.status];
  const reset = async () => {
    try {
      await api(`/api/customers/${c.id}/followup`, { body: { action: 'reset' } });
      qc.invalidateQueries({ queryKey: ['customer', c.id, 'buying'] });
      qc.invalidateQueries({ queryKey: ['followups'] });
      toast.success('הלקוח הוחזר למעקב ההזמנות');
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <section className="panel">
      <div className="panel-head">
        <h2>הרגלי קנייה</h2>
        <span className="spacer" />
        {data.status === 'due' || data.status === 'dormant' ? <Link to={`/followups?customer=${c.parentId ?? c.id}`} className="small">למעקב</Link> : null}
      </div>
      <div className="panel-body stack" style={{ gap: 10 }}>
        <dl className="kv">
          <dt>הזמנות</dt>
          <dd>{data.orders} · ממוצע {fmtMoney(data.avgOrder, { round: true })}</dd>
          <dt>קצב</dt>
          <dd>{data.usualEveryDays ? `כל ~${data.usualEveryDays} ימים` : '—'}</dd>
          <dt>הזמנה אחרונה</dt>
          <dd>{fmtDate(data.lastOrder)} ({data.daysSinceLastOrder} ימים)</dd>
        </dl>
        {statusText ? <span className={`badge ${data.status === 'due' || data.status === 'dormant' ? 'warn' : 'ok'}`} style={{ alignSelf: 'flex-start' }}>{statusText}</span> : null}
        {data.products.length ? (
          <div className="stack" style={{ gap: 6 }}>
            <span className="small strong">להציע שוב</span>
            <div className="row wrap" style={{ gap: 6 }}>{data.products.map((p) => <ProductChip key={p.category} p={p} />)}</div>
          </div>
        ) : null}
        {data.categories.length ? (
          <div className="small muted">
            קונה: {data.categories.map((p) => `${p.label} (${p.timesBought})`).join(' · ')}
          </div>
        ) : null}
        {data.crossSell.length ? <div className="small">לא קונה אצלנו: {data.crossSell.map((x) => x.label).join(', ')}</div> : null}
        {data.lastAction?.action === 'dismissed' ? (
          <div className="row small">
            <span className="muted">הוסר ממעקב ההזמנות</span>
            <Button size="sm" variant="ghost" onClick={reset}>החזרה למעקב</Button>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function BranchesPanel({ c }: { c: Customer }) {
  return (
    <section className="panel">
      <div className="panel-head"><h2>{c.parent ? 'לקוח אם' : t.profile.branches}</h2></div>
      <div className="panel-body stack" style={{ gap: 6 }}>
        {c.parent ? <Link to={`/customers/${c.parent.id}`}>{c.parent.businessName}</Link> : null}
        {c.children.map((ch) => (
          <div key={ch.id} className="row">
            <Link to={`/customers/${ch.id}`}>{ch.businessName}</Link>
            <span className="spacer" />
            {ch.status !== 'active' ? <CustomerStatusBadge status={ch.status} /> : null}
          </div>
        ))}
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ details

function Details({ c, onDelete }: { c: Customer; onDelete: () => void }) {
  const [editing, setEditing] = useState<Address | 'new' | null>(null);
  const [alias, setAlias] = useState('');
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const refresh = () => qc.invalidateQueries({ queryKey: ['customer', c.id] });

  const act = async (fn: () => Promise<unknown>, msg?: string) => {
    try {
      await fn();
      refresh();
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(e);
    }
  };

  return (
    <div className="grid-main-side">
      <div className="stack">
        <section className="panel">
          <div className="panel-head">
            <h2>{t.customerForm.business}</h2>
            <span className="spacer" />
            <Link to={`/customers/${c.id}/edit`} className="btn sm">{t.common.edit}</Link>
          </div>
          <div className="panel-body">
            <dl className="kv">
              <dt>{t.customerForm.businessName}</dt>
              <dd className="strong">{c.businessName}</dd>
              <dt>{t.customerForm.companyNumber}</dt>
              <dd>
                {c.companyNumber ? <Ltr className="tabular">{c.companyNumber}</Ltr> : <span className="muted">אין</span>}
                {c.companyNumberValid === false ? <span className="badge warn" style={{ marginInlineStart: 8 }}>ספרת ביקורת לא תקינה</span> : null}
              </dd>
              <dt>{t.customerForm.taxId}</dt>
              <dd>{c.taxId ?? <span className="muted">—</span>}</dd>
              <dt>{t.customerForm.contact}</dt>
              <dd>{c.contactName ?? <span className="muted">—</span>}</dd>
              <dt>{t.customerForm.phone}</dt>
              <dd>{c.phone ? <Ltr>{c.phone}</Ltr> : <span className="muted">—</span>}</dd>
              <dt>{t.customerForm.email}</dt>
              <dd>{c.email ? <bdi>{c.email}</bdi> : <span className="muted">—</span>}</dd>
              <dt>{t.customerForm.website}</dt>
              <dd>{c.website ? <a href={/^https?:/.test(c.website) ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer noopener"><bdi>{c.website}</bdi></a> : <span className="muted">—</span>}</dd>
              <dt>{t.customerForm.terms}</dt>
              <dd>{c.paymentTermsLabel}</dd>
              <dt>{t.customerForm.status}</dt>
              <dd><CustomerStatusBadge status={c.status} /></dd>
              <dt>נוצר</dt>
              <dd className="tabular">{fmtDateTime(c.createdAt)}</dd>
            </dl>
          </div>
        </section>

        <section className="panel">
          <div className="panel-head">
            <h2>{t.customerForm.addresses}</h2>
            <span className="spacer" />
            <Button size="sm" onClick={() => setEditing('new')}>
              <IconPlus /> {t.customerForm.addAddress}
            </Button>
          </div>
          {c.addresses.length === 0 ? (
            <Empty title={t.address.empty} body="הוסיפו את כתובות העסק: משרד ראשי, סניפים, מחסנים." />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>{t.address.label}</th>
                  <th>כתובת</th>
                  <th className="hide-sm">{t.address.contact}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {c.addresses.map((a) => (
                  <tr key={a.id}>
                    <td className="primary">
                      {a.label} {a.isPrimary ? <span className="badge accent" style={{ marginInlineStart: 6 }}>ראשית</span> : null}
                      {a.notes ? <span className="sub">{a.notes}</span> : null}
                    </td>
                    <td>{formatAddress(a) || <span className="muted">—</span>}</td>
                    <td className="hide-sm">
                      {a.contactName ?? <span className="faint">—</span>}
                      {a.phone ? <span className="sub"><Ltr>{a.phone}</Ltr></span> : null}
                    </td>
                    <td className="num nowrap">
                      {!a.isPrimary ? (
                        <Button size="sm" variant="ghost" onClick={() => act(() => api(`/api/addresses/${a.id}`, { method: 'PUT', body: { ...addressToPayload(toDraft(a)), isPrimary: true } }), 'הכתובת הוגדרה כראשית')}>
                          {t.address.makePrimary}
                        </Button>
                      ) : null}
                      <Button size="sm" variant="ghost" onClick={() => setEditing(a)}>{t.common.edit}</Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        icon
                        aria-label={t.common.delete}
                        onClick={async () => {
                          if (await confirm({ title: t.address.deleteConfirm, body: a.isPrimary ? 'זו הכתובת הראשית — כתובת אחרת תוגדר כראשית.' : undefined, danger: true, confirmLabel: t.common.delete }))
                            void act(() => api(`/api/addresses/${a.id}`, { method: 'DELETE' }), 'הכתובת נמחקה');
                        }}
                      >
                        <IconTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      <aside className="stack">
        <section className="panel">
          <div className="panel-head"><h2>{t.profile.aliases}</h2></div>
          <div className="panel-body stack" style={{ gap: 10 }}>
            <p className="small muted">{t.profile.aliasesHint}</p>
            {c.aliases.length ? (
              <div className="row wrap">
                {c.aliases.map((a) => (
                  <span key={a.id} className="badge outline">
                    {a.alias}
                    <button
                      className="toast-close"
                      style={{ color: 'var(--muted)' }}
                      aria-label={`הסרת ${a.alias}`}
                      onClick={() => act(() => api(`/api/aliases/${a.id}`, { method: 'DELETE' }))}
                    >
                      <IconX size={12} />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault();
                if (alias.trim().length < 2) return;
                void act(() => api(`/api/customers/${c.id}/aliases`, { body: { alias: alias.trim() } }), 'השם נוסף').then(() => setAlias(''));
              }}
            >
              <Input value={alias} onChange={(e) => setAlias(e.target.value)} placeholder="שם כפי שמופיע במסמכים" />
              <Button type="submit" size="sm" disabled={alias.trim().length < 2}>{t.profile.addAlias}</Button>
            </form>
          </div>
        </section>
        {c.children.length || c.parent ? <BranchesPanel c={c} /> : null}
        <section className="panel">
          <div className="panel-body">
            <Button variant="danger" size="sm" onClick={onDelete}>{t.profile.deleteCustomer}</Button>
          </div>
        </section>
      </aside>

      {editing ? <AddressDialog customerId={c.id} address={editing === 'new' ? null : editing} isFirst={c.addresses.length === 0} onClose={() => setEditing(null)} onSaved={refresh} /> : null}
    </div>
  );
}

const toDraft = (a: Address): AddressDraft => ({
  label: a.label,
  street: a.street ?? '',
  buildingNumber: a.buildingNumber ?? '',
  city: a.city ?? '',
  postalCode: a.postalCode ?? '',
  contactName: a.contactName ?? '',
  phone: a.phone ?? '',
  notes: a.notes ?? '',
  isPrimary: a.isPrimary,
});

function AddressDialog({ customerId, address, isFirst, onClose, onSaved }: { customerId: number; address: Address | null; isFirst: boolean; onClose: () => void; onSaved: () => void }) {
  const [draft, setDraft] = useState<AddressDraft>(address ? toDraft(address) : emptyAddress(isFirst));
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const save = async () => {
    setBusy(true);
    try {
      if (address) await api(`/api/addresses/${address.id}`, { method: 'PUT', body: addressToPayload(draft) });
      else await api(`/api/customers/${customerId}/addresses`, { body: addressToPayload(draft) });
      toast.success(t.common.savedToast);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={address ? 'עריכת כתובת' : t.customerForm.addAddress}
      footer={
        <>
          <Button variant="primary" onClick={save} loading={busy} disabled={!draft.label.trim()}>{t.common.save}</Button>
          <Button onClick={onClose}>{t.common.cancel}</Button>
        </>
      }
    >
      <AddressFields value={draft} onChange={setDraft} primaryLocked={Boolean(address?.isPrimary) || isFirst} />
    </Dialog>
  );
}

// ------------------------------------------------------------------ document tabs

function useSortState(initial: 'issueDate' | 'number' | 'total' | 'dueDate' | 'importedAt' = 'issueDate') {
  const [sort, setSort] = useState(initial);
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const onSort = (k: typeof sort) => {
    if (k === sort) setDir(dir === 'asc' ? 'desc' : 'asc');
    else {
      setSort(k);
      setDir('desc');
    }
  };
  return { sort, dir, onSort };
}

function QuotesTab({ c, branches }: { c: Customer; branches: boolean }) {
  const s = useSortState();
  const { data, isLoading } = useDocuments({ customerId: c.id, includeBranches: branches, types: 'quote', sort: s.sort, dir: s.dir, pageSize: 200 });
  return (
    <div className="stack">
      <div className="row">
        <span className="spacer" />
        <QuoteImportButton customerId={c.id}>
          <IconUpload /> ייבוא הצעת מחיר מ-Excel
        </QuoteImportButton>
      </div>
      {c.primaryQuote ? (
        <Callout tone="info" title={`${t.profile.primaryQuote}: הצעת מחיר ${c.primaryQuote.number ?? ''}`}>
          {fmtDate(c.primaryQuote.issueDate)} · {fmtMoney(c.primaryQuote.total)} {c.primaryQuote.status ? `· ${t.enums.quoteStatus[c.primaryQuote.status]}` : ''}
        </Callout>
      ) : null}
      <section className="panel">
        <DocTable
          docs={data?.items}
          loading={isLoading}
          {...s}
          columns={branches ? ['number', 'date', 'customer', 'description', 'total', 'quoteStatus', 'source'] : ['number', 'date', 'description', 'subtotal', 'total', 'quoteStatus', 'source', 'imported']}
          empty={<Empty title="אין הצעות מחיר" body="הצעות מחיר שתפיקו במורנינג יופיעו כאן אוטומטית." />}
        />
      </section>
    </div>
  );
}

function InvoicesTab({ c, branches }: { c: Customer; branches: boolean }) {
  const [filter, setFilter] = useState<'all' | 'invoices' | 'payments' | 'open'>('all');
  const s = useSortState();
  const types = { all: 'tax_invoice,tax_invoice_receipt,receipt,credit_note,proforma', invoices: 'tax_invoice,tax_invoice_receipt', payments: 'receipt,credit_note,tax_invoice_receipt', open: 'tax_invoice' }[filter];
  const { data, isLoading } = useDocuments({ customerId: c.id, includeBranches: branches, types, payment: filter === 'open' ? 'open' : undefined, sort: s.sort, dir: s.dir, pageSize: 200 });
  const can = useCan();
  return (
    <>
    <section className="panel">
      <div className="panel-head">
        <div className="segmented" role="group">
          {(['all', 'invoices', 'payments', 'open'] as const).map((k) => (
            <button key={k} className={filter === k ? 'active' : ''} onClick={() => setFilter(k)}>
              {{ all: 'הכל', invoices: 'חשבוניות', payments: 'קבלות וזיכויים', open: 'פתוחות לתשלום' }[k]}
            </button>
          ))}
        </div>
        <span className="spacer" />
        {data ? <span className="small muted tabular">{data.total} מסמכים</span> : null}
      </div>
      <DocTable
        selectable
        docs={data?.items}
        loading={isLoading}
        {...s}
        columns={[
          'type',
          'number',
          'date',
          ...(branches ? (['customer'] as const) : []),
          'subtotal',
          'margin',
          'vat',
          'total',
          'payment',
          'due',
          'source',
          'imported',
        ]}
        empty={<Empty title="אין מסמכים להצגה" body="חשבוניות וקבלות שתפיקו במורנינג ייכנסו לכאן אוטומטית." />}
      />
    </section>
    {can('finance.view') ? <CustomerArchiveInvoices customerId={c.id} /> : null}
    </>
  );
}

function DeliveryTab({ c, branches }: { c: Customer; branches: boolean }) {
  const [unbilled, setUnbilled] = useState(false);
  const s = useSortState();
  const { data, isLoading } = useDocuments({ customerId: c.id, includeBranches: branches, types: 'delivery_note', unbilled, sort: s.sort, dir: s.dir, pageSize: 200 });
  const sum = data?.items.reduce((acc, d) => acc + (d.total ?? 0), 0) ?? 0;
  return (
    <section className="panel">
      <div className="panel-head">
        <Checkbox checked={unbilled} onChange={setUnbilled}>{t.docs.unbilledOnly}</Checkbox>
        <span className="spacer" />
        {data && data.items.length ? <span className="small muted">סה״כ ברשימה: <span className="tabular strong" style={{ color: 'var(--ink)' }}>{fmtMoney(sum)}</span></span> : null}
      </div>
      <DocTable
        docs={data?.items}
        loading={isLoading}
        {...s}
        columns={['number', 'date', ...(branches ? (['customer'] as const) : []), 'address', 'total', 'margin', 'billed', 'source', 'imported']}
        empty={<Empty title="אין תעודות משלוח" body="תעודות משלוח מתווספות לכאן אוטומטית, ומסומנות כ״חויבו״ כשהחשבונית המרכזת מתייחסת אליהן." />}
      />
    </section>
  );
}

// ------------------------------------------------------------------ activity

function ActivityTab({ id }: { id: number }) {
  const { data, isLoading, error } = useCustomerActivity(id);
  if (error) return <div className="panel"><ErrorState error={error} /></div>;
  const dotClass = (type: string) => (/(invoice|receipt|credit|delivery|quote|document)/.test(type) ? 'doc' : /payment/.test(type) ? 'money' : '');
  return (
    <section className="panel">
      <div className="panel-body">
        {isLoading ? (
          <span className="skeleton" style={{ width: '50%' }} />
        ) : !data?.items.length ? (
          <Empty title="אין פעילות" />
        ) : (
          <ol className="timeline">
            {data.items.map((a) => (
              <li key={a.id}>
                <span className={`tl-dot ${dotClass(a.type)}`} />
                <div>
                  <div>{a.description}</div>
                  {a.userName ? <div className="small faint">{a.userName}</div> : <div className="small faint">אוטומטי</div>}
                </div>
                <span className="tl-time" title={fmtDateTime(a.createdAt)}>{fmtDateTime(a.createdAt)}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ history import

function BackfillDialog({ c, onClose }: { c: Customer; onClose: () => void }) {
  const { data: agent } = useAgentStatus(true);
  const toast = useToast();
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  const [since, setSince] = useState(d.toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [jobId, setJobId] = useState<number | null>(null);
  const job = agent?.jobs.find((j) => j.id === jobId) ?? (agent?.lastJob?.id === jobId ? agent?.lastJob : null);
  const connected = agent?.account?.status === 'connected';
  const qc = useQueryClient();

  const start = async () => {
    setBusy(true);
    try {
      const r = await api<{ jobId: number; agentAlive: boolean }>('/api/agent/backfill', { body: { since, customerId: c.id } });
      setJobId(r.jobId);
      if (!r.agentAlive) toast.error('המשימה נשמרה, אבל הסוכן אינו פועל כרגע — היא תתבצע כשהסוכן יופעל.');
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  const done = job?.status === 'done' || job?.status === 'failed';
  useEffect(() => {
    if (done) for (const k of ['customer', 'documents', 'ingestions']) void qc.invalidateQueries({ queryKey: [k] });
  }, [done, qc]);

  let body: ReactNode;
  if (!agent) body = <span className="spinner" />;
  else if (!connected)
    body = (
      <Callout tone="warn" title="לא מחוברת תיבת דואר" action={<Link to="/automation" className="btn sm">חיבור</Link>}>
        כדי לייבא היסטוריה יש לחבר קודם את תיבת הדואר העסקית.
      </Callout>
    );
  else if (jobId)
    body = (
      <div className="stack">
        <div className="row">
          {!done ? <span className="spinner" /> : null}
          <strong>{job?.status === 'done' ? 'הייבוא הסתיים' : job?.status === 'failed' ? 'הייבוא נכשל' : job?.status === 'running' ? 'מייבא…' : 'ממתין לסוכן…'}</strong>
        </div>
        {job?.progress?.total != null ? (
          <div className="small muted tabular">
            נסרקו {job.progress.scanned ?? 0} מתוך {job.progress.total} מיילים · יובאו {job.progress.imported ?? 0} מסמכים
          </div>
        ) : null}
        {'error' in (job ?? {}) && (job as { error?: string | null }).error ? <Callout tone="bad">{(job as { error: string }).error}</Callout> : null}
        {done ? <div className="small muted">מסמכים שהסוכן לא זיהה בוודאות ממתינים בתור הבדיקה.</div> : null}
      </div>
    );
  else
    body = (
      <div className="stack">
        <p>הסוכן יחפש בתיבת הדואר מסמכים ממורנינג שהופקו עבור <strong>{c.businessName}</strong> (לפי השם, שמות חלופיים והח.פ) וייבא אותם לכרטיס.</p>
        <Field label={t.agent.backfillSince}>
          <Input type="date" value={since} onChange={(e) => setSince(e.target.value)} max={new Date().toISOString().slice(0, 10)} />
        </Field>
        <p className="small muted">מסמכים שכבר קיימים במערכת לא ייובאו פעמיים.</p>
      </div>
    );

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${t.profile.importHistory} · ${c.businessName}`}
      footer={
        jobId ? (
          <Button onClick={onClose}>{t.common.close}</Button>
        ) : (
          <>
            <Button variant="primary" onClick={start} loading={busy} disabled={!connected || !since}>{t.agent.backfillStart}</Button>
            <Button onClick={onClose}>{t.common.cancel}</Button>
          </>
        )
      }
    >
      {body}
    </Dialog>
  );
}
